import { useMemo, useState, useEffect } from 'react';
import { FundingPolicyType } from '@shared/appModel';
import { fundingPolicyTypeDefs } from '@shared/FundingPolicyTypeDef';

export interface EnrichedQuoteFields {
  projectedCostCoverage?: number;
  partnerProjectedCostCoverage?: number;
  roi?: number;
  formattedROI?: string;
}

export type GuidedPlanningQuote = (PolicyQuote | TraditionalLTCQuote) & EnrichedQuoteFields;

interface GroupedQuotes {
  bestPerPolicyType: GuidedPlanningQuote[];
  otherQuotes: GuidedPlanningQuote[];
}

interface UseQuotesDisplayOptions {
  quotes: GuidedPlanningQuote[] | undefined;
  topQuotesLimit?: number;
  quoteIdFromUrl?: string | null;
}

interface UseQuotesDisplayResult {
  topQuotes: GuidedPlanningQuote[];
  remainingQuotes: GuidedPlanningQuote[];
  allQuotes: GuidedPlanningQuote[];
  totalCount: number;
  hasMore: boolean;
  selectedQuote: GuidedPlanningQuote | null;
  setSelectedQuote: (quote: GuidedPlanningQuote | null) => void;
}

export interface PolicyTypeFilterOption {
  policyType: FundingPolicyType;
  label: string;
  carrierName: string;
  logo: string;
  quoteCount: number;
}

export interface PaymentOptionFilterOption {
  value: string;
  label: string;
  quoteCount: number;
}

export function isTraditionalLTCQuote(
  quote: GuidedPlanningQuote,
): quote is TraditionalLTCQuote & EnrichedQuoteFields {
  return 'traditionalLTCQuoteId' in quote;
}

export function getQuoteId(quote: GuidedPlanningQuote): string {
  return isTraditionalLTCQuote(quote) ? quote.traditionalLTCQuoteId : quote.insuranceQuoteId;
}

export function getCarrierName(quote: GuidedPlanningQuote): string {
  return isTraditionalLTCQuote(quote) ? quote.carrier : quote.carrierName;
}

export function getPremiumAmount(quote: GuidedPlanningQuote): number {
  return isTraditionalLTCQuote(quote) ? quote.primaryModalPremium : quote.premiumAmount;
}

export function getProjectedCoverage(quote: GuidedPlanningQuote): number {
  return quote.projectedCostCoverage ?? 0;
}

export function getPartnerProjectedCoverage(quote: GuidedPlanningQuote): number | undefined {
  return quote.partnerProjectedCostCoverage;
}

export function getFormattedROI(quote: GuidedPlanningQuote): string | undefined {
  return quote.formattedROI;
}

export function getPolicyTypeLabel(fundingPolicyType: FundingPolicyType): string {
  return fundingPolicyTypeDefs[fundingPolicyType]?.label || 'Insurance Policy';
}

export function getPaymentOption(quote: GuidedPlanningQuote): string | undefined {
  if (isTraditionalLTCQuote(quote)) {
    return quote.premiumFrequency;
  }
  return quote.productFundingOption || quote.cobPaymentOption;
}

/**
 * Extracts unique policy type filter options from quotes.
 * Each option includes policy type label, a representative carrier, and logo.
 */
export function getPolicyTypeFilterOptions(
  quotes: GuidedPlanningQuote[],
): PolicyTypeFilterOption[] {
  const policyTypeMap = new Map<FundingPolicyType, { carrierName: string; count: number }>();

  for (const quote of quotes) {
    const policyType = quote.fundingPolicyType as FundingPolicyType;
    const existing = policyTypeMap.get(policyType);
    if (existing) {
      existing.count++;
    } else {
      policyTypeMap.set(policyType, {
        carrierName: getCarrierName(quote),
        count: 1,
      });
    }
  }

  const options: PolicyTypeFilterOption[] = [];
  for (const [policyType, data] of policyTypeMap) {
    options.push({
      policyType,
      label: fundingPolicyTypeDefs[policyType]?.label || 'Insurance Policy',
      carrierName: data.carrierName,
      logo: `/logos/${data.carrierName.toLowerCase()}.svg`,
      quoteCount: data.count,
    });
  }

  // Sort by quote count descending
  return options.sort((a, b) => b.quoteCount - a.quoteCount);
}

/**
 * Extracts unique payment option filter options from quotes.
 */
export function getPaymentOptionFilterOptions(
  quotes: GuidedPlanningQuote[],
): PaymentOptionFilterOption[] {
  const paymentOptionMap = new Map<string, number>();

  for (const quote of quotes) {
    const paymentOpt = getPaymentOption(quote);
    if (paymentOpt) {
      paymentOptionMap.set(paymentOpt, (paymentOptionMap.get(paymentOpt) ?? 0) + 1);
    }
  }

  const options: PaymentOptionFilterOption[] = [];
  for (const [value, count] of paymentOptionMap) {
    options.push({
      value,
      label: value,
      quoteCount: count,
    });
  }

  // Sort by quote count descending
  return options.sort((a, b) => b.quoteCount - a.quoteCount);
}

/**
 * Groups quotes by policy type, picks the highest ROI quote from each group,
 * and separates remaining quotes.
 */
function pickBestQuotePerPolicyType(quotes: GuidedPlanningQuote[]): GroupedQuotes {
  // Group by policy type
  const byPolicyType = new Map<FundingPolicyType, GuidedPlanningQuote[]>();
  for (const quote of quotes) {
    const type = quote.fundingPolicyType as FundingPolicyType;
    if (!byPolicyType.has(type)) {
      byPolicyType.set(type, []);
    }
    byPolicyType.get(type)!.push(quote);
  }

  const bestPerPolicyType: GuidedPlanningQuote[] = [];
  const otherQuotes: GuidedPlanningQuote[] = [];

  // From each group, pick highest ROI as "best", rest go to "other"
  for (const groupQuotes of byPolicyType.values()) {
    const sorted = [...groupQuotes].sort((a, b) => (b.roi ?? 0) - (a.roi ?? 0));
    bestPerPolicyType.push(sorted[0]);
    otherQuotes.push(...sorted.slice(1));
  }

  // Sort best quotes by ROI for display order
  bestPerPolicyType.sort((a, b) => (b.roi ?? 0) - (a.roi ?? 0));

  return { bestPerPolicyType, otherQuotes };
}

/**
 * Transforms a flat list of quotes into top quotes (best ROI per policy type)
 * and remaining quotes for display in the quotes step.
 * Also manages selected quote state and initializes from URL query param.
 *
 * - Groups quotes by policy type
 * - Picks the best (highest ROI) quote from each policy type
 * - Returns the top N (default 4) as "topQuotes"
 * - Returns everything else as "remainingQuotes" sorted by ROI
 * - Manages selected quote state and initializes from URL
 */
export function useQuotesDisplay({
  quotes,
  topQuotesLimit = 4,
  quoteIdFromUrl,
}: UseQuotesDisplayOptions): UseQuotesDisplayResult {
  const { topQuotes, remainingQuotes, allQuotes, totalCount, hasMore } = useMemo(() => {
    if (!quotes || quotes.length === 0) {
      return {
        topQuotes: [],
        remainingQuotes: [],
        allQuotes: [],
        totalCount: 0,
        hasMore: false,
      };
    }

    const { bestPerPolicyType, otherQuotes } = pickBestQuotePerPolicyType(quotes);

    // Sort other quotes by ROI for filling and remaining
    const sortedOtherQuotes = [...otherQuotes].sort((a, b) => (b.roi ?? 0) - (a.roi ?? 0));

    // Top quotes: best per policy type, filled with highest ROI others if needed
    const topQuotes = [...bestPerPolicyType, ...sortedOtherQuotes].slice(0, topQuotesLimit);

    // Remaining: everything not in topQuotes, sorted by ROI
    const topQuoteIds = new Set(topQuotes.map(getQuoteId));
    const remainingQuotes = [...bestPerPolicyType, ...sortedOtherQuotes]
      .filter(q => !topQuoteIds.has(getQuoteId(q)))
      .sort((a, b) => (b.roi ?? 0) - (a.roi ?? 0));

    // All quotes combined for searching
    const allQuotes = [...topQuotes, ...remainingQuotes];

    return {
      topQuotes,
      remainingQuotes,
      allQuotes,
      totalCount: quotes.length,
      hasMore: remainingQuotes.length > 0,
    };
  }, [quotes, topQuotesLimit]);

  const [selectedQuote, setSelectedQuote] = useState<GuidedPlanningQuote | null>(null);

  // Initialize selected quote from URL query param when quotes are loaded
  useEffect(() => {
    if (!quoteIdFromUrl) {
      setSelectedQuote(null);
      return;
    }

    if (allQuotes.length > 0) {
      const quote = allQuotes.find(q => getQuoteId(q) === quoteIdFromUrl) ?? null;
      setSelectedQuote(quote);
    }
  }, [quoteIdFromUrl, allQuotes]);

  return {
    topQuotes,
    remainingQuotes,
    allQuotes,
    totalCount,
    hasMore,
    selectedQuote,
    setSelectedQuote,
  };
}
